import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { FlaskConical, Droplets, CheckCircle2, FileCheck2, IndianRupee, AlertCircle } from "lucide-react";
import { api, apiError } from "@/lib/api";
import { PageHeader, Loading, ErrorState } from "@/components/Bits";
import { NoAccess } from "@/components/AppShell";
import { useAuth } from "@/context/AuthContext";

const Stat = ({ icon: Icon, label, value, tone = "slate", to }) => {
  const body = (
    <div className="panel p-5 flex items-center gap-4 h-full" data-testid={`lab-stat-${label.toLowerCase().replace(/[^a-z]+/g, "-")}`}>
      <div className={`h-11 w-11 rounded-xl grid place-items-center bg-${tone}-50 text-${tone}-600 shrink-0`}>
        <Icon className="h-5 w-5" strokeWidth={1.8} />
      </div>
      <div>
        <p className="text-2xl font-semibold text-slate-900">{value}</p>
        <p className="text-xs text-slate-500 mt-0.5">{label}</p>
      </div>
    </div>
  );
  return to ? <Link to={to}>{body}</Link> : body;
};

export default function LabDashboard() {
  const { can } = useAuth();
  const [d, setD] = useState(null);
  const [error, setError] = useState("");

  useEffect(() => {
    (async () => {
      try { const { data } = await api.get("/lab/dashboard"); setD(data); }
      catch (e) { setError(apiError(e)); }
    })();
  }, []);

  if (!can("lab.dashboard")) return <NoAccess what="the Laboratory module" />;

  return (
    <div data-testid="lab-dashboard">
      <PageHeader overline="Laboratory" title="Lab Dashboard"
        subtitle="Live view of the diagnostic workflow — from sample collection to verified, payment-cleared reports." />
      {error && <ErrorState message={error} />}
      {!d ? <Loading /> : (
        <>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
            <Stat icon={FlaskConical} label="Pending sample collection" value={d.pending_collection} tone="amber" to="/app/lab/orders?status=ordered" />
            <Stat icon={Droplets} label="In processing" value={d.in_progress} tone="blue" to="/app/lab/orders?status=in_progress" />
            <Stat icon={CheckCircle2} label="Awaiting verification" value={d.awaiting_verification} tone="violet" to="/app/lab/orders?status=completed" />
            <Stat icon={FileCheck2} label="Awaiting report release" value={d.awaiting_release} tone="emerald" to="/app/lab/orders?status=verified" />
            <Stat icon={IndianRupee} label="Collected revenue (₹)" value={d.collected_revenue.toLocaleString("en-IN")} tone="emerald" />
            <Stat icon={AlertCircle} label="Outstanding due (₹)" value={d.outstanding_due.toLocaleString("en-IN")} tone="rose" />
          </div>
          <div className="panel p-5 mt-5" data-testid="lab-policy-note">
            <p className="text-sm text-slate-700 mb-2">
              Report-release policy: <span className="font-semibold">
                {d.report_policy === "full" ? "Full payment required before print/download" : "No payment enforcement"}
              </span>.
            </p>
            {can("settings.manage") && (
              <select className="rounded-lg border border-slate-300 px-3 py-2 text-sm" data-testid="lab-policy-select"
                value={d.report_policy}
                onChange={async (e) => {
                  try { await api.put("/lab/settings", { report_policy: e.target.value }); setD({ ...d, report_policy: e.target.value }); }
                  catch (err) { setError(apiError(err)); }
                }}>
                <option value="full">Block report until full payment collected</option>
                <option value="none">Do not enforce payment for report release</option>
              </select>
            )}
          </div>
          <div className="panel p-5 mt-5" data-testid="lab-workflow-note">
            <p className="text-sm text-slate-700 mb-2">
              Laboratory Report Workflow: <span className="font-semibold">
                {d.report_workflow === "disabled" ? "Disabled — billing / collection only (external partner report)" : "Enabled — full technical sample → result → report workflow"}
              </span>.
            </p>
            {can("settings.manage") && (
              <select className="rounded-lg border border-slate-300 px-3 py-2 text-sm" data-testid="lab-workflow-select"
                value={d.report_workflow || "enabled"}
                onChange={async (e) => {
                  try { await api.put("/lab/settings", { report_workflow: e.target.value }); setD({ ...d, report_workflow: e.target.value }); }
                  catch (err) { setError(apiError(err)); }
                }}>
                <option value="enabled">Enabled — run sample collection, results, verification & report release</option>
                <option value="disabled">Disabled — Lab is a billing / collection point only</option>
              </select>
            )}
          </div>
        </>
      )}
    </div>
  );
}
